import { Cause, Context, Deferred, Effect, Exit, Layer, Queue, Redacted, Ref, Result, Schema, Scope } from "effect";
import { privateDependencyFailure } from "@workerstation/shared/errors/private-cause";
import { makeSessionOwner } from "../state/session-owner.ts";
import { KeyboardEventsIo, type KeydownListener } from "./keyboard-ports.ts";

export class MenuEscapeFailure extends privateDependencyFailure<MenuEscapeFailure>()("MenuEscapeFailure", "menu-escape") {}
const failed = (cause: unknown) => new MenuEscapeFailure({ cause: Redacted.make(cause) });
const Escape = Schema.Literal("Escape");
type Observation = { readonly _tag: "Key"; readonly value: unknown } | { readonly _tag: "Failed"; readonly failure: MenuEscapeFailure };
export class MenuEscapes extends Context.Service<MenuEscapes, {
  readonly listen: (close: Effect.Effect<void>) => Effect.Effect<void, never, Scope.Scope>;
}>()("workerstation/frontend/MenuEscapes") {}

export const MenuEscapesLive = (root: Scope.Scope) => Layer.effect(MenuEscapes, Effect.gen(function* () {
  const io = yield* KeyboardEventsIo, failures = yield* Ref.make<readonly Cause.Cause<never>[]>([]);
  yield* Effect.addFinalizer(() => Ref.get(failures).pipe(Effect.flatMap(values => values.length ? Effect.failCause(Cause.fromReasons(values.flatMap(value => value.reasons))) : Effect.void)));
  const sessions = yield* makeSessionOwner(root);
  const admissions = yield* Ref.make<{ readonly closed: boolean; readonly pending: ReadonlySet<Deferred.Deferred<void>> }>({ closed: false, pending: new Set() });
  yield* Effect.addFinalizer(() => Ref.getAndUpdate(admissions, current => ({ ...current, closed: true })).pipe(Effect.flatMap(current => Effect.forEach(current.pending, Deferred.await, { discard: true }))));
  return MenuEscapes.of({ listen: close => Effect.uninterruptible(Effect.gen(function* () {
    const caller = yield* Scope.Scope, done = yield* Deferred.make<void>();
    const admitted = yield* Ref.modify(admissions, current => current.closed || root.state._tag === "Closed" || caller.state._tag === "Closed"
      ? [false, current] as const : [true, { ...current, pending: new Set(current.pending).add(done) }] as const);
    if (!admitted) return yield* Effect.interrupt;
    const result = yield* Effect.exit(sessions.open(Effect.gen(function* () {
      const owner = yield* Scope.Scope, alive = () => root.state._tag !== "Closed" && caller.state._tag !== "Closed" && owner.state._tag !== "Closed" && !Ref.getUnsafe(admissions).closed;
      const queue = yield* Effect.acquireRelease(Queue.unbounded<Observation>(), Queue.shutdown);
      const receive: KeydownListener = event => {
        if (!alive()) return;
        const result = Result.try(() => event.key);
        Queue.offerUnsafe(queue, Result.isFailure(result) ? { _tag: "Failed", failure: failed(result.failure) } : { _tag: "Key", value: result.success });
      };
      yield* Effect.addFinalizer(() => io.remove(receive));
      yield* io.add(receive);
      yield* Effect.forkIn(Effect.forever(Effect.gen(function* () {
        const observation = yield* Queue.take(queue);
        if (!alive()) return yield* Effect.interrupt;
        const result = yield* Effect.exit(Effect.gen(function* () {
          if (observation._tag === "Failed") return yield* Effect.die(observation.failure);
          const decoded = yield* Effect.exit(Schema.decodeUnknownEffect(Escape)(observation.value));
          if (Exit.isSuccess(decoded) && alive()) yield* close;
        }));
        if (Exit.isFailure(result)) {
          if (!result.cause.reasons.some(Cause.isDieReason)) return yield* Effect.failCause(result.cause);
          yield* Ref.update(failures, values => [...values, result.cause]);
        }
      })), owner, { uninterruptible: false });
    }).pipe(Effect.onExit(exit => Exit.isFailure(exit) ? Ref.update(failures, values => [...values, exit.cause]) : Effect.void))));
    yield* Deferred.succeed(done, undefined);
    yield* Ref.update(admissions, current => { const pending = new Set(current.pending);pending.delete(done);return { ...current, pending }; });
    return yield* result;
  })) });
}));
